import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  SkipBack,
  Play,
  Pause,
  SkipForward,
  Repeat,
  Shuffle,
  Maximize2,
  Minimize2,
  Clock,
  X,
  Heart,
  Volume2,
} from 'lucide-react';
import { useFavorites } from '@/lib/hooks/use-favorites';
import { cn, formatTime, fixDropboxUrl, normalizeImageUrl, retryImageOnce } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { trackPlaytime } from '@/lib/activity';
import { useProfile } from '@/lib/providers';

interface AudioPlayerProps {
  id: string;
  title?: string;
  subtitle?: string;
  imageUrl?: string;
  audioUrl?: string;
  onNext?: () => void;
  onPrevious?: () => void;
  className?: string;
  hideControls?: ('shuffle' | 'repeat' | 'timer')[];
  shuffle?: boolean;
  onShuffleChange?: (shuffle: boolean) => void;
  type?: 'binaural' | 'music' | 'meditation' | 'natuurgeluid';
  shouldPlay?: boolean;
  onPlayStarted?: () => void;
  specificTips?: {
    start: string[];
    during: string[];
    end: string[];
  };
  volumeAboveTimeline?: boolean;
  /**
   * Hide the artwork thumbnail. For pages that already carry the context
   * around the player (the 7-daagse challenge).
   */
  minimal?: boolean;
  /** Fired the first time the user actually starts playback. */
  onPlaybackStart?: () => void;
  /** Fired whenever playback pauses. */
  onPlaybackPause?: () => void;
  /** Raise to pause from outside the player; cleared via onPlaybackPause. */
  shouldPause?: boolean;
  /**
   * Skip favourites and playtime tracking. Both write rows keyed on a
   * content UUID, so anything not backed by a `content` row (the 7-daagse
   * challenge's mock tracks, for instance) must opt out.
   */
  disableTracking?: boolean;
}

export default function AudioPlayer({
  title,
  subtitle,
  imageUrl,
  audioUrl,
  onNext,
  onPrevious,
  className,
  hideControls = [],
  shuffle,
  onShuffleChange,
  id,
  type,
  shouldPlay,
  onPlayStarted,
  specificTips,
  volumeAboveTimeline,
  disableTracking,
  minimal,
  onPlaybackStart,
  onPlaybackPause,
  shouldPause,
}: AudioPlayerProps) {
  imageUrl = normalizeImageUrl(imageUrl) || imageUrl;
  audioUrl = fixDropboxUrl(audioUrl) || audioUrl;

  const { profile } = useProfile();
  const { favorites, toggleFavorite } = useFavorites();
  const [isPlaying, setIsPlaying] = useState(false);
  const [curTimeSec, setCurTimeSec] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loop, setLoop] = useState(true); // loop mode (repeat current track)
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [timer, setTimer] = useState<number | null>(null); // minutes chosen
  const [timerRemaining, setTimerRemaining] = useState<number>(0); // seconds remaining
  const timerIntervalRef = useRef<NodeJS.Timeout>();
  const isIOS = (window as any).Capacitor?.getPlatform?.() === 'ios';
  const [volume, setVolume] = useState(1);
  const [showMobileVolume, setShowMobileVolume] = useState(false);
  const mobileVolumeRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const pendingPlayRef = useRef(false);
  const [initialLoad, setInitialLoad] = useState(false);

  const [trackPlayDur] = trackPlaytime(
    disableTracking ? null : id,
    profile?.id ?? null,
  );

  // Close mobile volume popup when clicking outside
  useEffect(() => {
    if (!showMobileVolume) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (mobileVolumeRef.current && !mobileVolumeRef.current.contains(e.target as Node)) {
        setShowMobileVolume(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileVolume]);

  // Pause audio when isPlaying becomes false (from timer, track end, etc.)
  // Note: play() is called directly from click handlers for mobile compatibility
  useEffect(() => {
    if (audioRef.current && !isPlaying) {
      audioRef.current.pause();
    }

    if (!isPlaying) return;
    const interval = setInterval(() => {
      if (audioRef.current) {
        trackPlayDur((prev) => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  useEffect(() => {
    if (!audioUrl) {
      setIsPlaying(false);
      setCurTimeSec(0);
      setDuration(0);
    }
  }, [audioUrl]);

  // When shouldPlay is signalled, auto-play if audio is ready or mark pending for when it loads
  useEffect(() => {
    if (shouldPlay) {
      onPlayStarted?.();
      if (audioRef.current && audioRef.current.readyState >= 2) {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch((err) => console.log('Playback prevented:', err));
      } else {
        pendingPlayRef.current = true;
      }
    }
  }, [shouldPlay]);

  useEffect(() => {
    if (timer && isPlaying) {
      timerIntervalRef.current = setInterval(() => {
        setTimerRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            setIsPlaying(false);
            setTimer(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (!isPlaying && timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [timer, isPlaying]);

  const handleTimeUpdate = async () => {
    if (!isPlaying) return;
    if (!audioRef.current) return;

    setCurTimeSec(audioRef.current.currentTime);

    if (audioRef.current.currentTime >= audioRef.current.duration) {
      if (loop) return; // automatically handled by loop attribute
      if (onNext) {
        onNext();
      } else {
        setIsPlaying(false);
      }
    }
  };

  useEffect(() => {
    if (shouldPause) audioRef.current?.pause();
  }, [shouldPause]);

  const handleLoadedMetadata = () => {
    setInitialLoad(true);
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      if (isPlaying || pendingPlayRef.current) {
        pendingPlayRef.current = false;
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch((err) => console.log('Playback prevented:', err));
      }
    }
  };

  const startTimer = (minutes: number) => {
    setTimer(minutes);
    setTimerRemaining(minutes * 60);
  };

  const clearTimer = () => {
    setTimer(null);
    setTimerRemaining(0);
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
  };

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const isPlayable = Boolean(audioUrl);
  const progress = (duration > 0 ? curTimeSec / duration : 0) * 100;

  // Fullscreen view rendered via portal to bypass parent positioning (e.g. lg:left-64)
  const fullscreenView = isFullscreen
    ? createPortal(
        <div className="fixed inset-0 z-[100] flex items-center bg-black text-white">
          {/* Blurred background image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
              filter: 'blur(20px)',
              transform: 'scale(1.2)',
            }}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/40" />

          {/* Back button */}
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-8 left-8 z-50 text-white hover:text-white/80 transition-colors cap-top-abs"
          >
            <X className="h-8 w-8" />
          </button>

          {/* Favorite button */}
          {id && type && !disableTracking && (
            <button
              onClick={() => toggleFavorite(id)}
              className="absolute top-8 right-8 z-50 text-white hover:text-white/80 transition-colors cap-top-abs"
            >
              <Heart
                className={cn(
                  'h-8 w-8',
                  favorites[id]
                    ? 'fill-red-500 stroke-red-500'
                    : 'stroke-white',
                )}
              />
            </button>
          )}

          <div className="w-full h-full flex flex-col items-center justify-center relative z-10 px-4">
            <div className="flex flex-col items-center space-y-8 w-full max-w-4xl">
              <div className="w-full max-w-[360px]">
                <div className="relative">
                  <img
                    src={imageUrl || '/images/logotrans.svg'}
                    alt={title}
                    className="w-full aspect-square rounded-lg object-cover shadow-2xl"
                    loading="lazy"
                    onError={retryImageOnce}
                  />
                </div>
              </div>
              <div className="w-full max-w-[900px] bg-black/40 backdrop-blur-sm p-4 sm:p-6 rounded-lg border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-medium">{title}</h3>
                    <p className="text-sm text-muted-foreground">{subtitle}</p>
                  </div>
                  <button
                    onClick={() => setIsFullscreen(false)}
                    className="p-2 hover:bg-white/10 rounded-full"
                  >
                    <Minimize2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  {volumeAboveTimeline && !isIOS && (
                    <div className="hidden sm:flex items-center gap-2">
                      <Volume2 className="h-4 w-4 text-white/40 flex-shrink-0" />
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          setVolume(v);
                          if (audioRef.current) audioRef.current.volume = v;
                        }}
                        className="flex-1 h-1 accent-white cursor-pointer"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-white/60">
                      {formatTime(curTimeSec)}
                    </span>
                    <div
                      className="flex-1 h-1 bg-gray-600 rounded-full cursor-pointer"
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const clickPosition = e.clientX - rect.left;
                        const percentageClicked = Math.min(
                          Math.max(clickPosition / rect.width, 0),
                          1,
                        );
                        if (audioRef.current) {
                          audioRef.current.currentTime =
                            percentageClicked * duration;
                        }
                      }}
                    >
                      <div
                        className="h-full bg-white rounded-full transition-all"
                        style={{ width: `${(curTimeSec / duration) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-white/60">
                      {formatTime(duration - curTimeSec)}
                    </span>
                  </div>

                  <div className="flex items-center">
                    <div className="flex-1" />
                    <div className="flex items-center gap-4 sm:gap-8">
                      {!hideControls.includes('repeat') && (
                        <button
                          className={cn(
                            'p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors',
                            loop ? 'text-green-500' : 'text-white/40 hover:text-white',
                          )}
                          onClick={() => setLoop((prev) => !prev)}
                        >
                          <Repeat className="h-5 w-5 sm:h-6 sm:w-6" />
                        </button>
                      )}

                      <button
                        className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full text-white/60 hover:text-white"
                        onClick={() => {
                          if (onPrevious) {
                            onPrevious();
                          } else if (audioRef.current) {
                            audioRef.current.currentTime = 0;
                            setCurTimeSec(0);
                          }
                        }}
                      >
                        <SkipBack className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" />
                      </button>

                      <button
                        className="p-2 sm:p-3 bg-white rounded-full hover:bg-white/90 transition-colors"
                        onClick={() => {
                          if (!audioRef.current) return;
                          if (isPlaying) {
                            audioRef.current.pause();
                            setIsPlaying(false);
                          } else {
                            audioRef.current.play()
                              .then(() => setIsPlaying(true))
                              .catch((err) => console.log('Playback prevented:', err));
                          }
                        }}
                        disabled={!isPlayable}
                      >
                        {isPlaying ? (
                          <Pause className="h-5 w-5 sm:h-6 sm:w-6 text-black" fill="black" />
                        ) : (
                          <Play className="h-5 w-5 sm:h-6 sm:w-6 text-black" fill="black" />
                        )}
                      </button>

                      {onNext && (
                        <button
                          className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full text-white/60 hover:text-white"
                          onClick={onNext}
                        >
                          <SkipForward className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" />
                        </button>
                      )}

                      {!hideControls.includes('timer') && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className={cn(
                                'flex items-center gap-1 sm:gap-1.5 p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors',
                                timer ? 'text-green-500' : 'text-white/40 hover:text-white',
                              )}
                            >
                              <Clock className="h-5 w-5 sm:h-6 sm:w-6" />
                              {timer && (
                                <span className="text-xs font-medium">{formatCountdown(timerRemaining)}</span>
                              )}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 z-[110]">
                            <div className="px-2 py-1.5 text-sm font-semibold">
                              Stop met afspelen over:
                            </div>
                            <DropdownMenuItem onClick={() => startTimer(5)}>
                              5 min
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => startTimer(10)}>
                              10 min
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => startTimer(20)}>
                              20 min
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => startTimer(30)}>
                              30 min
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => startTimer(45)}>
                              45 min
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => startTimer(60)}>
                              60 min
                            </DropdownMenuItem>
                            {timer && (
                              <DropdownMenuItem
                                onClick={() => clearTimer()}
                                className="text-red-500"
                              >
                                Timer Annuleren
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>

                    {!volumeAboveTimeline && !isIOS && (
                      <>
                        {/* Mobile volume popup in fullscreen */}
                        <div className="flex-1 flex sm:hidden items-center justify-end">
                          <div ref={mobileVolumeRef} className="relative">
                            <button
                              className="p-1.5 hover:bg-white/10 rounded-full text-white/60 hover:text-white transition-colors"
                              onClick={() => setShowMobileVolume(!showMobileVolume)}
                            >
                              <Volume2 className="h-5 w-5" />
                            </button>
                            {showMobileVolume && (
                              <div className="absolute bottom-full right-0 mb-2 z-50 bg-black/80 backdrop-blur-sm rounded-lg p-3 border border-white/10 flex flex-col items-center gap-2">
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.01"
                                  value={volume}
                                  onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    setVolume(v);
                                    if (audioRef.current) audioRef.current.volume = v;
                                  }}
                                  className="h-24 accent-white cursor-pointer"
                                  style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                                />
                                <Volume2 className="h-4 w-4 text-white/40" />
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Desktop volume slider in fullscreen */}
                        <div className="hidden sm:flex flex-1 items-center justify-end gap-2">
                          <Volume2 className="h-6 w-6 text-white/40" />
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={volume}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              setVolume(v);
                              if (audioRef.current) audioRef.current.volume = v;
                            }}
                            className="w-24 h-1 accent-white cursor-pointer"
                          />
                        </div>
                      </>
                    )}
                    {/* Keeps the controls centred when there is no volume slider (iOS, or volume above the timeline) */}
                    {(volumeAboveTimeline || isIOS) && <div className="flex-1" />}
                  </div>
                </div>
              </div>

              {/* Specific Tips */}
              {specificTips && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 w-full max-w-[900px]">
                  <div className="space-y-3 bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <h3 className="font-semibold text-white">Bij Aanvang</h3>
                    </div>
                    <ul className="text-sm text-white/80 space-y-1 list-disc pl-4">
                      {specificTips.start.map((tip, i) => (
                        <li key={i}>{tip}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-3 bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-orange-500" />
                      <h3 className="font-semibold text-white">Tijdens</h3>
                    </div>
                    <ul className="text-sm text-white/80 space-y-1 list-disc pl-4">
                      {specificTips.during.map((tip, i) => (
                        <li key={i}>{tip}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-3 bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <h3 className="font-semibold text-white">
                        Bij Afsluiting
                      </h3>
                    </div>
                    <ul className="text-sm text-white/80 space-y-1 list-disc pl-4">
                      {specificTips.end.map((tip, i) => (
                        <li key={i}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      {fullscreenView}
      <div
        className={cn(
          'flex items-center space-x-4 bg-black/40 backdrop-blur-sm p-4 rounded-none sm:rounded-lg border border-white/10 text-white transition-all duration-300',
          className,
        )}
      >
        {!isFullscreen && (
        <>
          {!minimal && (
          <div className="relative z-10 hidden lg:block">
            <img
              src={imageUrl || '/images/logotrans.svg'}
              alt={title}
              className="h-24 w-24 rounded-md object-cover"
              loading="lazy"
              onError={retryImageOnce}
            />
          </div>
          )}
          <div className="flex-1 relative z-10">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-medium">{title}</h3>
                <p className="text-xs text-muted-foreground hidden sm:block">{subtitle}</p>
              </div>
              <div className="flex items-center gap-1 ml-4">
                {!hideControls.includes('timer') && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          'flex sm:hidden items-center gap-1.5 p-2 hover:bg-white/10 rounded-full transition-colors',
                          timer ? 'text-green-500' : 'text-white/40 hover:text-white',
                        )}
                      >
                        <Clock className="h-4 w-4" />
                        {timer && (
                          <span className="text-xs font-medium">{formatCountdown(timerRemaining)}</span>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <div className="px-2 py-1.5 text-sm font-semibold">
                        Stop met afspelen over:
                      </div>
                      <DropdownMenuItem onClick={() => startTimer(5)}>
                        5 min
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => startTimer(10)}>
                        10 min
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => startTimer(20)}>
                        20 min
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => startTimer(30)}>
                        30 min
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => startTimer(45)}>
                        45 min
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => startTimer(60)}>
                        60 min
                      </DropdownMenuItem>
                      {timer && (
                        <DropdownMenuItem
                          onClick={() => clearTimer()}
                          className="text-red-500"
                        >
                          Timer Annuleren
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <button
                  onClick={() => setIsFullscreen(true)}
                  className="p-2 hover:bg-white/10 rounded-full"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {volumeAboveTimeline && !isIOS && (
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-white/40 flex-shrink-0" />
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      setVolume(v);
                      if (audioRef.current) audioRef.current.volume = v;
                    }}
                    className="flex-1 h-1 accent-white cursor-pointer"
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/60">
                  {formatTime(curTimeSec)}
                </span>
                <div
                  className="flex-1 h-1 bg-gray-600 rounded-full cursor-pointer"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const clickPosition = e.clientX - rect.left;
                    const percentageClicked = Math.min(
                      Math.max(clickPosition / rect.width, 0),
                      1,
                    );
                    if (audioRef.current) {
                      audioRef.current.currentTime =
                        percentageClicked * duration;
                    }
                  }}
                >
                  <div
                    className="h-full bg-white rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-xs text-white/60">
                  {formatTime(duration - curTimeSec)}
                </span>
              </div>

              <div className="flex items-center">
                {/* Left controls */}
                <div className="flex-1 flex items-center justify-end gap-2 sm:gap-6">
                  {!hideControls.includes('shuffle') && (
                    <button
                      className={cn(
                        'p-1.5 hover:bg-white/10 rounded-full transition-colors',
                        shuffle ? 'text-green-500' : 'text-white/40 hover:text-white',
                      )}
                      onClick={() => onShuffleChange?.(!shuffle)}
                    >
                      <Shuffle className="h-4 w-4 sm:h-6 sm:w-6" />
                    </button>
                  )}

                  <button
                    className="p-1.5 hover:bg-white/10 rounded-full text-white/60 hover:text-white"
                    onClick={() => {
                      if (onPrevious) {
                        onPrevious();
                      } else if (audioRef.current) {
                        audioRef.current.currentTime = 0;
                        setCurTimeSec(0);
                      }
                    }}
                  >
                    <SkipBack className="h-4 w-4 sm:h-6 sm:w-6" fill="currentColor" />
                  </button>
                </div>

                {/* Play button (centered) */}
                <button
                  className="p-2 mx-4 sm:mx-6 bg-white rounded-full hover:bg-white/90 transition-colors"
                  onClick={() => {
                    if (!audioRef.current) return;
                    if (isPlaying) {
                      audioRef.current.pause();
                      setIsPlaying(false);
                    } else {
                      audioRef.current.play()
                        .then(() => setIsPlaying(true))
                        .catch((err) => console.log('Playback prevented:', err));
                    }
                  }}
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5 text-black" fill="black" />
                  ) : (
                    <Play className="h-5 w-5 text-black" fill="black" />
                  )}
                </button>

                {/* Right controls */}
                <div className="flex-1 flex items-center gap-2 sm:gap-6">
                  {onNext && (
                    <button
                      className="p-1.5 hover:bg-white/10 rounded-full text-white/60 hover:text-white"
                      onClick={onNext}
                    >
                      <SkipForward className="h-4 w-4 sm:h-6 sm:w-6" fill="currentColor" />
                    </button>
                  )}

                  {!hideControls.includes('repeat') && (
                    <button
                      className={cn(
                        'p-1.5 hover:bg-white/10 rounded-full transition-colors',
                        loop ? 'text-green-500' : 'text-white/40 hover:text-white',
                      )}
                      onClick={() => setLoop((prev) => !prev)}
                    >
                      <Repeat className="h-4 w-4 sm:h-6 sm:w-6" />
                    </button>
                  )}

                  {!hideControls.includes('timer') && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={cn(
                            'hidden sm:flex items-center gap-1.5 p-1.5 hover:bg-white/10 rounded-full transition-colors',
                            timer ? 'text-green-500' : 'text-white/40 hover:text-white',
                          )}
                        >
                          <Clock className="h-6 w-6" />
                          {timer && (
                            <span className="text-xs font-medium">{formatCountdown(timerRemaining)}</span>
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <div className="px-2 py-1.5 text-sm font-semibold">
                          Stop met afspelen over:
                        </div>
                        <DropdownMenuItem onClick={() => startTimer(5)}>
                          5 min
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => startTimer(10)}>
                          10 min
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => startTimer(20)}>
                          20 min
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => startTimer(30)}>
                          30 min
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => startTimer(45)}>
                          45 min
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => startTimer(60)}>
                          60 min
                        </DropdownMenuItem>
                        {timer && (
                          <DropdownMenuItem
                            onClick={() => clearTimer()}
                            className="text-red-500"
                          >
                            Timer Annuleren
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  <div className="flex-1" />

                  {/* Mobile volume button with vertical popup */}
                  <div ref={mobileVolumeRef} className={cn('relative sm:hidden', (volumeAboveTimeline || isIOS) && 'hidden')}>
                    <button
                      className="p-1.5 hover:bg-white/10 rounded-full text-white/60 hover:text-white transition-colors"
                      onClick={() => setShowMobileVolume(!showMobileVolume)}
                    >
                      <Volume2 className="h-6 w-6" />
                    </button>
                    {showMobileVolume && (
                      <div className="absolute bottom-full right-0 mb-2 z-50 bg-black/80 backdrop-blur-sm rounded-lg p-3 border border-white/10 flex flex-col items-center gap-2">
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={volume}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            setVolume(v);
                            if (audioRef.current) audioRef.current.volume = v;
                          }}
                          className="h-24 accent-white cursor-pointer"
                          style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                        />
                        <Volume2 className="h-4 w-4 text-white/40" />
                      </div>
                    )}
                  </div>
                  {/* Desktop volume slider */}
                  <div className={cn('hidden sm:flex items-center gap-2', (volumeAboveTimeline || isIOS) && 'sm:hidden')}>
                    <Volume2 className="h-6 w-6 text-white/40" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setVolume(v);
                        if (audioRef.current) audioRef.current.volume = v;
                      }}
                      className="w-20 h-1 accent-white cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
        <audio
          ref={audioRef}
          src={audioUrl}
          loop={loop}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => {
            if (!initialLoad) return;
            setIsPlaying(true);
            onPlaybackStart?.();
          }}
          onPause={() => {
            setIsPlaying(false);
            onPlaybackPause?.();
          }}
          autoPlay={false}
          playsInline
          preload="metadata"
        />
      </div>
    </>
  );
}
